<template>
  <div class="public-charges-page">
    <main class="public-charges-shell">
      <header class="public-header">
        <img
          v-if="logoUrl"
          :src="logoUrl"
          alt="Logomarca"
          class="public-logo"
          referrerpolicy="no-referrer"
        />

        <div>
          <p class="eyebrow">
            Área pública de pagamento
          </p>

          <h1>
            Cobranças do contrato
          </h1>

          <p
            v-if="dados"
            class="contract-number"
          >
            Contrato
            <strong>
              {{ dados.lote.numero_contrato }}
            </strong>
          </p>
        </div>
      </header>

      <section
        v-if="loading"
        class="state-card"
        aria-live="polite"
      >
        <div
          class="spinner-border"
          role="status"
          aria-hidden="true"
        />

        <p>
          Carregando suas cobranças...
        </p>
      </section>

      <section
        v-else-if="errorMessage"
        class="state-card state-error"
        role="alert"
      >
        <i
          class="bi bi-exclamation-circle"
          aria-hidden="true"
        />

        <h2>
          Link indisponível
        </h2>

        <p>
          {{ errorMessage }}
        </p>

        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="carregar"
        >
          Tentar novamente
        </button>
      </section>

      <template v-else-if="dados">
        <section
          class="readiness-card"
          :class="
            readinessClass(
              dados.lote.readiness
            )
          "
        >
          <div>
            <span class="readiness-label">
              {{
                readinessLabel(
                  dados.lote.readiness
                )
              }}
            </span>

            <p>
              {{
                readinessMessage(
                  dados.lote.readiness
                )
              }}
            </p>
          </div>

          <div class="readiness-count">
            <strong>
              {{
                dados.lote
                  .quantidade_disponiveis
              }}
            </strong>
            de
            {{
              dados.lote
                .quantidade_itens
            }}
            disponíveis
          </div>
        </section>

        <div class="page-actions">
          <button
            type="button"
            class="btn btn-outline-primary"
            :disabled="loading"
            @click="carregar"
          >
            <i
              class="bi bi-arrow-clockwise"
              aria-hidden="true"
            />
            Atualizar
          </button>
        </div>

        <div class="charges-list">
          <article
            v-for="parcela in dados.parcelas"
            :key="parcela.ordem"
            class="charge-card"
          >
            <div class="charge-card-header">
              <div>
                <p class="charge-order">
                  Título {{ parcela.ordem }}
                </p>

                <h2>
                  <template
                    v-if="
                      parcela.numero_parcela !==
                        null
                    "
                  >
                    Parcela
                    {{ parcela.numero_parcela }}
                  </template>

                  <template v-else>
                    Cobrança
                  </template>
                </h2>
              </div>

              <span
                class="status-pill"
                :class="
                  statusClass(
                    parcela.status
                  )
                "
              >
                {{
                  statusLabel(
                    parcela.status
                  )
                }}
              </span>
            </div>

            <dl class="charge-summary">
              <div>
                <dt>
                  Valor
                </dt>

                <dd>
                  {{
                    formatarValor(
                      parcela.valor
                    )
                  }}
                </dd>
              </div>

              <div>
                <dt>
                  Vencimento
                </dt>

                <dd>
                  {{
                    formatarData(
                      parcela
                        .data_vencimento
                    )
                  }}
                </dd>
              </div>

              <div
                v-if="
                  parcela.competencia
                "
              >
                <dt>
                  Competência
                </dt>

                <dd>
                  {{
                    formatarCompetencia(
                      parcela.competencia
                    )
                  }}
                </dd>
              </div>
            </dl>

            <div
              v-if="
                !parcela
                  .disponivel_pagamento
              "
              class="payment-pending"
            >
              <i
                class="bi bi-clock-history"
                aria-hidden="true"
              />

              <div>
                <strong>
                  Dados de pagamento ainda não disponíveis
                </strong>

                <p>
                  Este título continuará aparecendo aqui.
                  Atualize esta página mais tarde.
                </p>
              </div>
            </div>

            <template v-else>
              <section
                v-if="parcela.boleto"
                class="payment-section"
              >
                <h3>
                  Boleto
                </h3>

                <div
                  v-if="
                    parcela.boleto
                      .linha_digitavel
                  "
                  class="copy-box"
                >
                  <div>
                    <span class="copy-label">
                      Linha digitável
                    </span>

                    <code>
                      {{
                        parcela.boleto
                          .linha_digitavel
                      }}
                    </code>
                  </div>

                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm"
                    @click="
                      copiarTexto(
                        parcela.boleto
                          .linha_digitavel,
                        `boleto-${parcela.ordem}`
                      )
                    "
                  >
                    <i
                      class="bi bi-copy"
                      aria-hidden="true"
                    />

                    Copiar
                  </button>
                </div>

                <div class="payment-actions">
                  <button v-if="parcela.boleto.documento_local_disponivel" type="button" class="btn btn-primary" :disabled="documentoLoading" @click="abrirDocumento(parcela.ordem)">
                    Visualizar boleto
                  </button>
                  <a
                    v-if="
                      parcela.boleto
                        .url_boleto
                    "
                    :href="
                      parcela.boleto
                        .url_boleto
                    "
                    class="btn btn-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerpolicy="no-referrer"
                  >
                    <i
                      class="bi bi-box-arrow-up-right"
                      aria-hidden="true"
                    />

                    Abrir boleto
                  </a>

                  <a
                    v-if="
                      parcela.boleto
                        .url_pdf
                    "
                    :href="
                      parcela.boleto
                        .url_pdf
                    "
                    class="btn btn-outline-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerpolicy="no-referrer"
                  >
                    <i
                      class="bi bi-file-earmark-pdf"
                      aria-hidden="true"
                    />

                    Abrir PDF
                  </a>
                </div>
              </section>

              <section
                v-if="parcela.pix"
                class="payment-section pix-section"
              >
                <h3>
                  Pix
                </h3>

                <div class="pix-layout">
                  <img
                    v-if="
                      parcela.pix
                        .qrcode_url
                    "
                    :src="
                      parcela.pix
                        .qrcode_url
                    "
                    alt="QR Code Pix"
                    class="pix-qr"
                    referrerpolicy="no-referrer"
                  />

                  <div
                    v-if="
                      parcela.pix
                        .copia_cola
                    "
                    class="copy-box pix-copy"
                  >
                    <div>
                      <span class="copy-label">
                        Pix copia e cola
                      </span>

                      <code>
                        {{
                          parcela.pix
                            .copia_cola
                        }}
                      </code>
                    </div>

                    <button
                      type="button"
                      class="btn btn-outline-success btn-sm"
                      @click="
                        copiarTexto(
                          parcela.pix
                            .copia_cola,
                          `pix-${parcela.ordem}`
                        )
                      "
                    >
                      <i
                        class="bi bi-copy"
                        aria-hidden="true"
                      />

                      Copiar Pix
                    </button>
                  </div>
                </div>
              </section>
            </template>

            <p
              v-if="
                copyFeedbackKey ===
                  `boleto-${parcela.ordem}` ||
                copyFeedbackKey ===
                  `pix-${parcela.ordem}`
              "
              class="copy-feedback"
              aria-live="polite"
            >
              {{ copyFeedbackMessage }}
            </p>
          </article>
        </div>
      </template>

      <p v-if="documentoErro" role="alert" class="text-danger">{{ documentoErro }}</p>
      <VisualizadorDocumentoCobranca v-if="documento" :visible="true" initial-mode="boleto"
        :cobranca="documento.cobranca" :boleto="documento.boleto" :empresa-publica="documento.empresa"
        :aviso-documento="documento.aviso" @close="documento = null" />
      <footer class="public-footer">
        <p>
          Consulte sempre esta página pelo link recebido.
          Os dados exibidos refletem a situação atual dos títulos.
        </p>
      </footer>
    </main>
  </div>
</template>

<script setup>
import VisualizadorDocumentoCobranca from '@/components/Financeiro/Cobrancas/VisualizadorDocumentoCobranca.vue';
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from 'vue';

import {
  useRoute
} from 'vue-router';

import {
  publicAxios
} from '@/services/publicAxios';

const documento = ref(null);
const documentoLoading = ref(false);
const documentoErro = ref('');
let documentoRequest = 0;
async function abrirDocumento(ordem) {
  const request = ++documentoRequest;
  documento.value = null;
  documentoErro.value = '';
  documentoLoading.value = true;
  try {
    const { data } = await publicAxios.get(`/public/cobrancas/lotes/${encodeURIComponent(String(route.params.token || ''))}/documentos/${ordem}`);
    if (request !== documentoRequest) return;
    if (data?.origem !== 'GERACAO_LOCAL' || !data.empresa || !data.cobranca || !data.boleto) throw new Error('DOCUMENTO_INVALIDO');
    documento.value = data;
  } catch {
    if (request === documentoRequest) documentoErro.value = 'Este documento não está disponível. Atualize a página ou solicite um novo link.';
  } finally {
    if (request === documentoRequest) documentoLoading.value = false;
  }
}

const route =
  useRoute();

const {
  proxy
} =
  getCurrentInstance();

const dados =
  ref(null);

const loading =
  ref(true);

const errorMessage =
  ref('');

const copyFeedbackKey =
  ref('');

const copyFeedbackMessage =
  ref('');

let copyFeedbackTimer =
  null;

const logoUrl =
  computed(
    () =>
      proxy
        ?.$globalState
        ?.logoPath ||
      ''
  );

const statusLabels =
  Object.freeze({
    RASCUNHO:
      'Em preparação',

    REGISTRO_PENDENTE:
      'Processando',

    REGISTRADA:
      'Disponível',

    REJEITADA:
      'Não disponível',

    VENCIDA:
      'Vencida',

    ALTERACAO_PENDENTE:
      'Atualizando',

    BAIXA_PENDENTE:
      'Processando baixa',

    BAIXADA:
      'Baixada',

    LIQUIDADA:
      'Liquidada',

    CREDITADA:
      'Creditada',

    ESTORNADA:
      'Estornada',

    CANCELADA:
      'Cancelada',

    SUBSTITUIDA:
      'Substituída',

    ERRO:
      'Indisponível'
  });

function ensureNoReferrerPolicy() {
  if (
    typeof document ===
      'undefined'
  ) {
    return;
  }

  let meta =
    document.querySelector(
      'meta[name="referrer"]'
    );

  if (!meta) {
    meta =
      document.createElement(
        'meta'
      );

    meta.setAttribute(
      'name',
      'referrer'
    );

    document.head.appendChild(
      meta
    );
  }

  meta.setAttribute(
    'content',
    'no-referrer'
  );
}

function isPlainObject(
  value
) {
  return (
    value !== null &&
    typeof value ===
      'object' &&
    !Array.isArray(
      value
    )
  );
}

function payloadPublicoValido(
  value
) {
  return (
    isPlainObject(value) &&
    isPlainObject(
      value.lote
    ) &&
    typeof value.lote
      .numero_contrato ===
      'string' &&
    typeof value.lote
      .readiness ===
      'string' &&
    Number.isSafeInteger(
      value.lote
        .quantidade_itens
    ) &&
    Number.isSafeInteger(
      value.lote
        .quantidade_disponiveis
    ) &&
    Array.isArray(
      value.parcelas
    )
  );
}

async function carregar() {
  ++documentoRequest;
  documento.value = null;
  documentoLoading.value = false;
  documentoErro.value = '';
  loading.value =
    true;

  errorMessage.value =
    '';

  dados.value =
    null;

  /*
   * A credencial existe apenas na rota.
   * Não é copiada para estado, storage,
   * query string ou log.
   */
  const credential =
    String(
      route.params.token ||
      ''
    );

  try {
    const {
      data
    } =
      await publicAxios.get(
        `/public/cobrancas/lotes/${
          encodeURIComponent(
            credential
          )
        }`
      );

    if (
      !payloadPublicoValido(
        data
      )
    ) {
      throw new TypeError(
        'PUBLIC_RESPONSE_INVALID'
      );
    }

    dados.value =
      data;
  } catch (error) {
    const status =
      error?.response?.status;

    errorMessage.value =
      status === 404
        ? 'Este link de cobranças não está disponível.'
        : 'Não foi possível carregar este link de cobranças.';
  } finally {
    loading.value =
      false;
  }
}

function formatarValor(
  value
) {
  const text =
    String(
      value ??
      ''
    ).trim();

  const match =
    /^([0-9]+)\.([0-9]{2})$/
      .exec(
        text
      );

  if (!match) {
    return '—';
  }

  const inteiro =
    match[1]
      .replace(
        /\B(?=(\d{3})+(?!\d))/g,
        '.'
      );

  return `R$ ${inteiro},${match[2]}`;
}

function formatarData(
  value
) {
  const match =
    /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/
      .exec(
        String(
          value ??
          ''
        )
      );

  if (!match) {
    return '—';
  }

  return `${
    match[3]
  }/${
    match[2]
  }/${
    match[1]
  }`;
}

function formatarCompetencia(
  value
) {
  const match =
    /^([0-9]{4})-([0-9]{2})-[0-9]{2}$/
      .exec(
        String(
          value ??
          ''
        )
      );

  if (!match) {
    return '—';
  }

  return `${
    match[2]
  }/${
    match[1]
  }`;
}

function readinessLabel(
  value
) {
  if (
    value ===
      'PRONTO_ENVIO'
  ) {
    return 'Títulos disponíveis';
  }

  if (
    value ===
      'PARCIALMENTE_PRONTO'
  ) {
    return 'Disponibilidade parcial';
  }

  if (
    value ===
      'NAO_PRONTO'
  ) {
    return 'Títulos em preparação';
  }

  return 'Situação atual';
}

function readinessMessage(
  value
) {
  if (
    value ===
      'PRONTO_ENVIO'
  ) {
    return 'Todos os títulos deste contrato estão disponíveis para pagamento.';
  }

  if (
    value ===
      'PARCIALMENTE_PRONTO'
  ) {
    return 'Alguns títulos já estão disponíveis. Os demais aparecerão nesta mesma página assim que forem liberados.';
  }

  return 'Os títulos ainda estão sendo disponibilizados. Atualize esta página mais tarde.';
}

function readinessClass(
  value
) {
  if (
    value ===
      'PRONTO_ENVIO'
  ) {
    return 'readiness-ready';
  }

  if (
    value ===
      'PARCIALMENTE_PRONTO'
  ) {
    return 'readiness-partial';
  }

  return 'readiness-pending';
}

function statusLabel(
  value
) {
  return (
    statusLabels[value] ||
    String(
      value ||
      '—'
    )
      .replaceAll(
        '_',
        ' '
      )
  );
}

function statusClass(
  value
) {
  if (
    [
      'REGISTRADA',
      'LIQUIDADA',
      'CREDITADA'
    ].includes(value)
  ) {
    return 'status-success';
  }

  if (
    [
      'RASCUNHO',
      'REGISTRO_PENDENTE',
      'ALTERACAO_PENDENTE',
      'BAIXA_PENDENTE'
    ].includes(value)
  ) {
    return 'status-warning';
  }

  if (
    [
      'REJEITADA',
      'VENCIDA',
      'ESTORNADA',
      'CANCELADA',
      'ERRO'
    ].includes(value)
  ) {
    return 'status-danger';
  }

  return 'status-secondary';
}

function fallbackCopy(
  text
) {
  if (
    typeof document ===
      'undefined'
  ) {
    return false;
  }

  const textarea =
    document.createElement(
      'textarea'
    );

  textarea.value =
    text;

  textarea.setAttribute(
    'readonly',
    ''
  );

  textarea.style.position =
    'fixed';

  textarea.style.opacity =
    '0';

  document.body.appendChild(
    textarea
  );

  textarea.select();

  let copied =
    false;

  try {
    copied =
      document.execCommand(
        'copy'
      );
  } catch {
    copied =
      false;
  }

  textarea.remove();

  return copied;
}

function showCopyFeedback(
  key,
  message
) {
  copyFeedbackKey.value =
    key;

  copyFeedbackMessage.value =
    message;

  if (
    copyFeedbackTimer
  ) {
    clearTimeout(
      copyFeedbackTimer
    );
  }

  copyFeedbackTimer =
    setTimeout(
      () => {
        copyFeedbackKey.value =
          '';

        copyFeedbackMessage.value =
          '';
      },
      2500
    );
}

async function copiarTexto(
  value,
  key
) {
  const text =
    String(
      value ||
      ''
    );

  if (!text) {
    return;
  }

  try {
    if (
      navigator
        ?.clipboard
        ?.writeText
    ) {
      await navigator
        .clipboard
        .writeText(
          text
        );

      showCopyFeedback(
        key,
        'Conteúdo copiado.'
      );

      return;
    }

    if (
      fallbackCopy(
        text
      )
    ) {
      showCopyFeedback(
        key,
        'Conteúdo copiado.'
      );

      return;
    }
  } catch {
    // Usa mensagem genérica abaixo.
  }

  showCopyFeedback(
    key,
    'Não foi possível copiar o conteúdo.'
  );
}

ensureNoReferrerPolicy();

onMounted(
  carregar
);

watch(
  () =>
    route.params.token,

  () => {
    carregar();
  }
);

onBeforeUnmount(
  () => {
    ++documentoRequest;
    if (
      copyFeedbackTimer
    ) {
      clearTimeout(
        copyFeedbackTimer
      );
    }
  }
);
</script>

<style scoped>
.public-charges-page {
  min-height: 100vh;
  background:
    linear-gradient(
      180deg,
      #f4f7fb 0%,
      #eef2f7 100%
    );
  color: #1f2937;
  padding: 28px 16px 48px;
}

.public-charges-shell {
  width: min(100%, 920px);
  margin: 0 auto;
}

.public-header {
  display: flex;
  align-items: center;
  gap: 24px;
  background: #fff;
  border-radius: 14px;
  padding: 24px 28px;
  box-shadow:
    0 8px 28px
    rgba(15, 23, 42, 0.08);
  margin-bottom: 18px;
}

.public-logo {
  display: block;
  width: auto;
  height: 84px;
  max-width: 220px;
  object-fit: contain;
}

.eyebrow {
  margin: 0 0 5px;
  color: #64748b;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.public-header h1 {
  margin: 0;
  font-size: clamp(
    1.45rem,
    4vw,
    2rem
  );
  color: #0f172a;
}

.contract-number {
  margin: 8px 0 0;
  color: #475569;
}

.state-card {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 14px;
  text-align: center;
  background: #fff;
  border-radius: 14px;
  padding: 30px;
  box-shadow:
    0 8px 28px
    rgba(15, 23, 42, 0.08);
}

.state-card p,
.state-card h2 {
  margin: 0;
}

.state-error > i {
  font-size: 2rem;
  color: #b91c1c;
}

.readiness-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  border-radius: 12px;
  padding: 18px 22px;
  margin-bottom: 12px;
  border: 1px solid transparent;
}

.readiness-card p {
  margin: 4px 0 0;
  font-size: 0.92rem;
}

.readiness-label {
  display: block;
  font-weight: 700;
}

.readiness-ready {
  background: #ecfdf3;
  border-color: #bbf7d0;
  color: #166534;
}

.readiness-partial {
  background: #fffbeb;
  border-color: #fde68a;
  color: #92400e;
}

.readiness-pending {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1e40af;
}

.readiness-count {
  flex: 0 0 auto;
  font-size: 0.9rem;
}

.readiness-count strong {
  font-size: 1.25rem;
}

.page-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 14px;
}

.page-actions .btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.charges-list {
  display: grid;
  gap: 16px;
}

.charge-card {
  background: #fff;
  border-radius: 14px;
  padding: 22px;
  box-shadow:
    0 8px 24px
    rgba(15, 23, 42, 0.07);
  border: 1px solid #e5e7eb;
}

.charge-card-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.charge-order {
  margin: 0 0 3px;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.charge-card h2 {
  margin: 0;
  color: #0f172a;
  font-size: 1.18rem;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 10px;
  white-space: nowrap;
  font-size: 0.78rem;
  font-weight: 700;
}

.status-success {
  color: #166534;
  background: #dcfce7;
}

.status-warning {
  color: #92400e;
  background: #fef3c7;
}

.status-danger {
  color: #991b1b;
  background: #fee2e2;
}

.status-secondary {
  color: #475569;
  background: #e2e8f0;
}

.charge-summary {
  display: grid;
  grid-template-columns:
    repeat(
      3,
      minmax(0, 1fr)
    );
  gap: 12px;
  margin: 18px 0 0;
}

.charge-summary > div {
  background: #f8fafc;
  border-radius: 9px;
  padding: 12px 14px;
}

.charge-summary dt {
  margin: 0 0 4px;
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 700;
  text-transform: uppercase;
}

.charge-summary dd {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.payment-pending {
  display: flex;
  gap: 12px;
  margin-top: 18px;
  background: #f8fafc;
  border-radius: 9px;
  padding: 14px;
  color: #475569;
}

.payment-pending > i {
  color: #64748b;
  font-size: 1.2rem;
}

.payment-pending p {
  margin: 3px 0 0;
  font-size: 0.9rem;
}

.payment-section {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid #e5e7eb;
}

.payment-section h3 {
  margin: 0 0 12px;
  font-size: 1rem;
  color: #0f172a;
}

.copy-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  padding: 12px;
}

.copy-box > div {
  min-width: 0;
  flex: 1;
}

.copy-label {
  display: block;
  margin-bottom: 4px;
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 700;
}

.copy-box code {
  display: block;
  color: #1e293b;
  white-space: normal;
  overflow-wrap: anywhere;
  font-size: 0.82rem;
}

.payment-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 12px;
}

.payment-actions .btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.pix-layout {
  display: flex;
  gap: 18px;
  align-items: center;
}

.pix-qr {
  width: 154px;
  height: 154px;
  flex: 0 0 154px;
  padding: 8px;
  object-fit: contain;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
}

.pix-copy {
  flex: 1;
  min-width: 0;
}

.copy-feedback {
  margin: 12px 0 0;
  color: #166534;
  font-size: 0.86rem;
  font-weight: 600;
}

.public-footer {
  color: #64748b;
  text-align: center;
  font-size: 0.82rem;
  padding: 24px 12px 0;
}

.public-footer p {
  margin: 0;
}

@media (max-width: 700px) {
  .public-charges-page {
    padding:
      12px
      10px
      28px;
  }

  .public-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 20px;
    gap: 14px;
  }

  .public-logo {
    max-width: 180px;
    height: 68px;
  }

  .readiness-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .charge-card {
    padding: 17px;
  }

  .charge-card-header {
    flex-direction: column;
  }

  .charge-summary {
    grid-template-columns: 1fr;
  }

  .copy-box {
    flex-direction: column;
    align-items: stretch;
  }

  .copy-box .btn {
    width: 100%;
  }

  .payment-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .payment-actions .btn {
    justify-content: center;
  }

  .pix-layout {
    flex-direction: column;
    align-items: stretch;
  }

  .pix-qr {
    align-self: center;
  }

  .page-actions .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
